import React, { useState, useEffect } from 'react';
import useTestimonials from '../../hooks/useTestimonials';
import { Testimonial } from '../../services/api';

const TestimonialsManagement: React.FC = () => {
  const { testimonials, loading, error, updateTestimonial, deleteTestimonial, approveTestimonial } = useTestimonials(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('approved');
  
  // Debug information
  useEffect(() => {
    console.log('TestimonialsManagement component:');
    console.log('- testimonials:', testimonials);
    console.log('- loading:', loading);
    console.log('- error:', error);
  }, [testimonials, loading, error]);
  
  // Make sure testimonials array exists before filtering
  const filteredTestimonials = testimonials ? testimonials.filter(testimonial => 
    activeTab === 'approved' ? testimonial.approved : !testimonial.approved
  ) : [];

  const handleApprove = async (id: string) => {
    const result = await approveTestimonial(id);
    if (!result.success) {
      alert('Failed to approve testimonial. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this testimonial? This action cannot be undone.')) {
      const result = await deleteTestimonial(id);
      if (!result.success) {
        alert('Failed to delete testimonial. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div data-testid="loading-spinner" className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
        <p className="text-sm mt-2">Please try again later or contact support.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-6 text-primary">Testimonials Management</h2>
        
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('approved')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'approved'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Approved
              {testimonials.filter(t => t.approved).length > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {testimonials.filter(t => t.approved).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Approval
              {testimonials.filter(t => !t.approved).length > 0 && (
                <span className="ml-2 bg-yellow-100 text-yellow-600 py-0.5 px-2 rounded-full text-xs">
                  {testimonials.filter(t => !t.approved).length}
                </span>
              )}
            </button>
          </nav>
        </div>
        
        {filteredTestimonials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {activeTab === 'approved' 
                ? 'No approved testimonials yet.'
                : 'No pending testimonials to review.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredTestimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                onApprove={handleApprove}
                onDelete={handleDelete}
                isPending={activeTab === 'pending'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface TestimonialCardProps {
  testimonial: Testimonial;
  onApprove: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isPending: boolean;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
  testimonial, 
  onApprove, 
  onDelete, 
  isPending 
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{testimonial.name}</h3>
            {testimonial.role && (
              <p className="text-sm text-gray-500">{testimonial.role}</p>
            )}
          </div>
          {testimonial.rating && (
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <span 
                  key={i} 
                  className={`text-lg ${i < testimonial.rating! ? 'text-yellow-400' : 'text-gray-300'}`}
                >
                  ★
                </span>
              ))}
            </div>
          )}
        </div>
        
        <blockquote className="mt-3 text-gray-700">
          "{testimonial.content}"
        </blockquote>
        
        <div className="mt-4 text-sm text-gray-500">
          {testimonial.date && new Date(testimonial.date).toLocaleDateString()}
        </div>
      </div>
      
      <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-between">
        {isPending ? (
          <button
            onClick={() => {
              console.log('Approve button clicked. ID:', testimonial.id);
              onApprove(testimonial.id!);
            }}
            className="text-sm font-medium text-green-600 hover:text-green-800"
          >
            Approve
          </button>
        ) : (
          <div></div> // Empty div to maintain space
        )}
        
        <button
          onClick={() => {
            console.log('Delete button clicked. ID:', testimonial.id);
            onDelete(testimonial.id!);
          }}
          className="text-sm font-medium text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default TestimonialsManagement;